<template>
  <div
    class="overflow-hidden p-6 mx-auto w-full max-w-md text-left text-white align-middle transition-all transform">
    <h3 class="text-lg font-medium leading-6">Delete Your Account</h3>

    <div class="mt-4 space-y-3">
      <p class="text-sm">
        Are you sure you want to delete your account? This action will:
      </p>
      <ul class="space-y-1 text-sm list-disc list-inside text-gray-200">
        <li>Remove all your reviews and ratings</li>
        <li>Delete your lists and saved content</li>
        <li>Remove your profile and followers</li>
      </ul>
      <p class="text-sm font-medium">This cannot be undone.</p>
    </div>

    <div class="mt-4">
      <label for="deletion-reason" class="block mb-1 text-sm font-medium">
        Why are you leaving? (optional)
      </label>
      <textarea
        id="deletion-reason"
        v-model="reason"
        rows="3"
        :disabled="isSubmitting"
        class="py-2 px-3 w-full text-sm text-white bg-gray-700 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-red-800 focus:border-red-800 disabled:bg-gray-900"
        placeholder="Your feedback helps us improve..."></textarea>
    </div>

    <div
      v-if="error"
      class="p-3 mt-3 bg-red-50 rounded-md border border-red-800">
      <p class="text-sm text-red-700">{{ error }}</p>
    </div>

    <div class="flex flex-row-reverse gap-2 mt-6">
      <button
        type="button"
        :disabled="isSubmitting"
        class="inline-flex justify-center py-2 px-4 text-sm font-medium text-white bg-red-700 rounded-md border border-transparent cursor-pointer hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="handleDelete">
        <span v-if="isSubmitting" class="flex items-center">
          <fa-icon
            :icon="['fas', 'spinner-third']"
            class="mr-2 -ml-1 w-4 h-4 text-white animate-spin" />
          Deleting...
        </span>
        <span v-else>Delete My Account</span>
      </button>
      <button
        type="button"
        :disabled="isSubmitting"
        class="inline-flex justify-center py-2 px-4 text-sm font-medium bg-gray-700 rounded-md border border-transparent cursor-pointer hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
        @click="closeModal">
        Cancel
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreAuth } from '@/stores/storeAuth'

const router = useRouter()
const storeProfile = useStoreProfile()
const storeAuth = useStoreAuth()

const emit = defineEmits(['close'])

const reason = ref('')
const isSubmitting = ref(false)
const error = ref(null)

const closeModal = () => {
  emit('close')
}

async function handleDelete() {
  isSubmitting.value = true
  error.value = null

  try {
    await storeProfile.requestAccountDeletion(reason.value || null)

    // Close modal
    closeModal()

    // Log user out and redirect
    await storeAuth.clearSession()
    router.push('/')
  } catch (err) {
    console.error('Failed to request account deletion:', err)
    error.value =
      err.message || 'Failed to submit deletion request. Please try again.'
    isSubmitting.value = false
  }
}
</script>
